import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  CheckCheck,
  Hash,
  Lock,
  MessageSquarePlus,
  Pencil,
  Plus,
  Search,
  Send,
  Shield,
  SmilePlus,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, Textarea } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/shared/page';
import { FormError } from '@/features/auth/auth-layout';
import { useSession } from '@/features/auth/use-session';
import { useUserOptions } from '@/hooks/use-user-options';
import { usePermission } from '@/hooks/use-permission';
import { ApiRequestError } from '@/lib/api';
import { formatDate, timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useChatChannels,
  useChatMessages,
  useChatRoles,
  useDeleteMessage,
  useEditMessage,
  useJoinChannel,
  useLeaveChannel,
  useMarkRead,
  useOpenDirect,
  useOpenGroup,
  useReact,
  useSaveChannel,
  useSendMessage,
  useUpdateChannelMembers,
  type ChatChannel,
  type ChatMember,
  type ChatMessage,
} from './api';

/** The reactions worth one tap. A full picker is a lot of weight for this. */
const QUICK_REACTIONS = ['👍', '🎉', '👀', '✅', '❤️', '😄'];

/**
 * How tall the chat screen is.
 *
 * Normally a CSS calculation: the viewport minus the shell's header and the
 * phone's tab bar. `100dvh` rather than `100vh`, because on a phone `100vh`
 * includes the strip behind the browser's own toolbar and the composer ended
 * up hidden under it. When the on-screen keyboard opens, iOS shrinks the
 * *visual* viewport but not the layout one, so for that case the height comes
 * from `visualViewport` directly and the page is pinned to the top — the
 * composer stays above the keyboard instead of scrolling off with it.
 */
function useChatHeight(): string {
  const [pinned, setPinned] = useState<number | null>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const update = () => {
      const keyboardOpen = window.innerHeight - viewport.height > 120;
      if (!keyboardOpen) {
        setPinned(null);
        return;
      }
      window.scrollTo(0, 0);
      // --app-header is in rem; 3.5rem unless the stylesheet says otherwise.
      const headerRem =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--app-header')) || 3.5;
      setPinned(viewport.height - headerRem * 16);
    };

    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, []);

  return pinned ? `${pinned}px` : 'calc(100dvh - var(--app-header) - var(--bottom-nav))';
}

/**
 * Internal chat.
 *
 * Two panes: the conversations on the left, the open one on the right. The
 * open conversation polls; the list polls more slowly. Nothing here is
 * permission-gated — everyone with an account can talk to their colleagues,
 * and what they may read is decided by which channels they are in.
 *
 * Opening chat lands on the list, never inside somebody's conversation.
 * Dropping a person into a thread they did not choose marks it read on their
 * behalf and buries the one they came for.
 */
export function ChatPage() {
  const { channelId } = useParams<{ channelId: string }>();
  const { data: me } = useSession();

  const { data, isLoading } = useChatChannels();
  const markRead = useMarkRead();

  const channels = data?.channels ?? [];
  const active = channels.find((channel) => channel.id === channelId);

  useEffect(() => {
    if (channelId && active && (active.unread ?? 0) > 0) {
      markRead.mutate(channelId);
    }
    // Only when the channel changes or new messages land in it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, active?.unread]);

  const height = useChatHeight();

  return (
    // Fills the shell's content area exactly: the negative margins cancel the
    // page padding, and the height is what is left after the header and, on a
    // phone, the tab bar — so the composer sits on the bottom edge and only
    // the messages scroll. See useChatHeight for the keyboard.
    <div className="-m-4 flex lg:-m-6" style={{ height }}>
      <ChannelList
        channels={channels}
        discoverable={data?.discoverable ?? []}
        activeId={channelId}
        loading={isLoading}
      />

      {/* On a phone the right pane exists only while a conversation is open;
          the list takes the whole screen otherwise. On a desktop both show. */}
      <div className={cn('min-w-0 flex-1', channelId ? 'flex' : 'hidden md:flex')}>
        {active ? (
          <Conversation key={active.id} channel={active} meId={me?.id ?? ''} />
        ) : (
          <div className="flex flex-1 items-center justify-center p-8">
            <EmptyState
              icon={<MessageSquarePlus className="h-6 w-6" />}
              title={isLoading ? 'Loading…' : 'No conversation open'}
              description="Pick a channel on the left, or start a direct message with a colleague."
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── The left pane ───────────────────────────────────────────────────────────

function ChannelList({
  channels,
  discoverable,
  activeId,
  loading,
}: {
  channels: ChatChannel[];
  discoverable: { id: string; name: string | null; topic: string | null }[];
  activeId?: string;
  loading: boolean;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [composing, setComposing] = useState<'channel' | 'direct' | null>(null);

  const join = useJoinChannel();
  const openDirect = useOpenDirect();
  const { data: me } = useSession();
  const { data: users } = useUserOptions();

  const rooms = channels.filter(
    (channel) =>
      channel.kind === 'PUBLIC' || channel.kind === 'PRIVATE' || channel.kind === 'ROLE',
  );
  const people = channels.filter((channel) => channel.kind === 'DIRECT' || channel.kind === 'GROUP');

  // WhatsApp shows everybody, not just the people you have already written
  // to. Colleagues without a conversation yet are listed beneath the ones
  // with; tapping one opens the direct message, creating it if needed.
  const alreadyTalking = new Set(
    people
      .filter((channel) => channel.kind === 'DIRECT')
      .flatMap((channel) => channel.members.map((member) => member.userId)),
  );
  const colleagues = (users ?? []).filter(
    (user) => user.id !== me?.id && !alreadyTalking.has(user.id),
  );
  const matchUser = (user: { name: string }) =>
    !query || user.name.toLowerCase().includes(query.toLowerCase());

  const match = (channel: ChatChannel) =>
    !query || channel.title.toLowerCase().includes(query.toLowerCase());

  return (
    <aside
      className={cn(
        'w-full shrink-0 flex-col border-r bg-muted/20 md:flex md:w-64 lg:w-72',
        // Full-screen list on a phone until a conversation is opened.
        activeId ? 'hidden' : 'flex',
      )}
    >
      <div className="flex items-center gap-2 border-b p-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Find a conversation"
            className="h-8 pl-8 text-sm"
          />
        </div>
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setComposing('direct')}>
          <Plus className="h-4 w-4" />
          <span className="sr-only">New conversation</span>
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {loading && <p className="px-2 py-4 text-sm text-muted-foreground">Loading…</p>}

        <Section
          label="Channels"
          action={
            <button
              onClick={() => setComposing('channel')}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              New
            </button>
          }
        >
          {rooms.filter(match).map((channel) => (
            <ChannelRow key={channel.id} channel={channel} active={channel.id === activeId} />
          ))}
          {rooms.length === 0 && !loading && (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">You are in no channels yet.</p>
          )}
        </Section>

        <Section label="Direct messages">
          {people.filter(match).map((channel) => (
            <ChannelRow key={channel.id} channel={channel} active={channel.id === activeId} />
          ))}
          {colleagues.filter(matchUser).map((user) => (
            <button
              key={user.id}
              disabled={openDirect.isPending}
              onClick={() =>
                openDirect.mutate(user.id, {
                  onSuccess: (channel) => navigate(`/chat/${channel.id}`),
                })
              }
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-60"
            >
              <Avatar name={user.name} size={20} />
              <span className="min-w-0 flex-1 truncate">{user.name}</span>
              {user.designation && (
                <span className="truncate text-xs text-muted-foreground/70">{user.designation}</span>
              )}
            </button>
          ))}
          {people.length === 0 && colleagues.length === 0 && !loading && (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">Nobody else is in the workspace yet.</p>
          )}
        </Section>

        {discoverable.length > 0 && (
          <Section label="Channels you can join">
            {discoverable.map((channel) => (
              <button
                key={channel.id}
                onClick={() =>
                  join.mutate(channel.id, { onSuccess: () => navigate(`/chat/${channel.id}`) })
                }
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Hash className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{channel.name}</span>
                <span className="ml-auto text-xs">Join</span>
              </button>
            ))}
          </Section>
        )}
      </div>

      {composing && (
        <ComposeDialog kind={composing} onClose={() => setComposing(null)} />
      )}
    </aside>
  );
}

function Section({
  label,
  action,
  children,
}: {
  label: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <div className="mb-1 flex items-center justify-between px-2">
        <span className="eyebrow text-[11px] text-muted-foreground">{label}</span>
        {action}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function ChannelRow({ channel, active }: { channel: ChatChannel; active: boolean }) {
  const navigate = useNavigate();
  const unread = channel.unread ?? 0;

  return (
    <button
      onClick={() => navigate(`/chat/${channel.id}`)}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
        active ? 'bg-background font-medium shadow-sm' : 'hover:bg-muted',
        !active && unread > 0 && 'font-medium',
      )}
    >
      {channel.kind === 'PRIVATE' ? (
        <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      ) : channel.kind === 'ROLE' ? (
        <Shield className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      ) : channel.kind === 'PUBLIC' ? (
        <Hash className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      ) : channel.kind === 'GROUP' ? (
        <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      ) : (
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
      )}
      <span className="truncate">{channel.title}</span>
      {unread > 0 && (
        <Badge className="ml-auto h-5 min-w-5 justify-center px-1.5 text-[11px]">{unread}</Badge>
      )}
    </button>
  );
}

// ── The right pane ──────────────────────────────────────────────────────────

function Conversation({ channel, meId }: { channel: ChatChannel; meId: string }) {
  const { data, isLoading, loadOlder } = useChatMessages(channel.id);
  const send = useSendMessage(channel.id);
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [showMembers, setShowMembers] = useState(false);
  const navigate = useNavigate();

  const scroller = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);

  const messages = data?.messages ?? [];

  // Follow the conversation only when the reader is already at the bottom.
  // Yanking someone back down while they are reading history is the single
  // most irritating thing a chat window can do.
  useEffect(() => {
    if (atBottom.current && scroller.current) {
      scroller.current.scrollTop = scroller.current.scrollHeight;
    }
  }, [messages.length]);

  const onScroll = () => {
    const element = scroller.current;
    if (!element) return;
    atBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
  };

  const submit = () => {
    const body = draft.trim();
    if (!body || send.isPending) return;

    const mentions = channel.members
      .filter((member) => body.includes(`@${member.user.name}`))
      .map((member) => member.userId);

    send.mutate(
      { body, mentions, replyToId: replyTo?.id },
      {
        onSuccess: () => {
          setDraft('');
          setReplyTo(null);
          atBottom.current = true;
        },
      },
    );
  };

  return (
    <section className="flex min-w-0 flex-1 flex-col">
      <header className="flex items-center gap-3 border-b px-4 py-3">
        {/* Phone only: the way back to the list, exactly where a thumb expects it. */}
        <button
          type="button"
          onClick={() => navigate('/chat')}
          className="-ml-1 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground md:hidden"
          aria-label="Back to conversations"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold">{channel.title}</h2>
          <p className="truncate text-xs text-muted-foreground">
            {channel.topic ??
              `${channel.members.length} ${channel.members.length === 1 ? 'person' : 'people'}`}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <div className="hidden -space-x-2 sm:flex">
            {channel.members.slice(0, 5).map((member) => (
              <Avatar key={member.userId} name={member.user.name} size={26} className="ring-2 ring-background" />
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={() => setShowMembers(true)}>
            <Users className="mr-1.5 h-3.5 w-3.5" />
            Members
          </Button>
        </div>
      </header>

      <div ref={scroller} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {data?.hasMore && (
          <div className="mb-4 text-center">
            <Button size="sm" variant="outline" onClick={() => void loadOlder()}>
              Load earlier messages
            </Button>
          </div>
        )}

        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {!isLoading && messages.length === 0 && (
          <p className="py-10 text-center text-sm text-muted-foreground">
            Nothing here yet. Say something.
          </p>
        )}

        <MessageStream
          messages={messages}
          meId={meId}
          members={channel.members}
          channelId={channel.id}
          onReply={setReplyTo}
        />
      </div>

      {/* On a phone this is the bottom edge of the screen: keep the composer
          clear of the home indicator. */}
      <footer className="shrink-0 border-t p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {replyTo && (
          <div className="mb-2 flex items-start gap-2 rounded-md border-l-2 border-primary bg-muted/50 px-3 py-1.5 text-xs">
            <div className="min-w-0 flex-1">
              <span className="font-medium">{replyTo.author?.name ?? 'Someone'}</span>
              <p className="truncate text-muted-foreground">{replyTo.body}</p>
            </div>
            <button onClick={() => setReplyTo(null)} className="text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <div className="flex items-end gap-2">
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter breaks the line — the convention
              // everybody's fingers already know.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            rows={1}
            placeholder={`Message ${channel.title}`}
            /* A textarea's natural width is about twenty characters, and a flex
               item will not shrink below its natural width on its own. That is
               what pushed the send button off the right of a phone screen. */
            className="max-h-40 min-h-[38px] min-w-0 flex-1 resize-none"
          />
          <Button
            onClick={submit}
            disabled={!draft.trim() || send.isPending}
            size="icon"
            className="shrink-0"
          >
            <Send className="h-4 w-4" />
            <span className="sr-only">Send</span>
          </Button>
        </div>
        {send.isError && (
          <div className="mt-2">
            <FormError message={(send.error as ApiRequestError).message} />
          </div>
        )}
      </footer>

      {showMembers && <MembersDialog channel={channel} meId={meId} onClose={() => setShowMembers(false)} />}
    </section>
  );
}

function MessageStream({
  messages,
  meId,
  members,
  channelId,
  onReply,
}: {
  messages: ChatMessage[];
  meId: string;
  members: ChatMember[];
  channelId: string;
  onReply: (message: ChatMessage) => void;
}) {
  const edit = useEditMessage(channelId);
  const remove = useDeleteMessage(channelId);
  const react = useReact(channelId, meId);
  const [editing, setEditing] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  // One day heading per day, and consecutive messages from the same person
  // within five minutes collapse into one block.
  const rendered = useMemo(() => {
    let lastDay = '';
    let lastAuthor = '';
    let lastAt = 0;

    return messages.map((message) => {
      const at = new Date(message.createdAt).getTime();
      const day = message.createdAt.slice(0, 10);
      const newDay = day !== lastDay;
      const grouped =
        !newDay &&
        !message.isSystem &&
        message.author?.id === lastAuthor &&
        at - lastAt < 5 * 60 * 1000;

      lastDay = day;
      lastAuthor = message.isSystem ? '' : (message.author?.id ?? '');
      lastAt = at;

      return { message, newDay, grouped };
    });
  }, [messages]);

  return (
    <div className="space-y-0.5">
      {rendered.map(({ message, newDay, grouped }) => (
        <div key={message.id}>
          {newDay && (
            <div className="my-4 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">{formatDate(message.createdAt)}</span>
              <div className="h-px flex-1 bg-border" />
            </div>
          )}

          {message.isSystem ? (
            <p className="py-1 text-center text-xs text-muted-foreground">{message.body}</p>
          ) : (
            <div className={cn('group relative flex gap-3 rounded-md px-2 py-1 hover:bg-muted/50', grouped && 'py-0.5')}>
              <div className="w-8 shrink-0">
                {!grouped && <Avatar name={message.author?.name ?? '?'} size={32} />}
              </div>

              <div className="min-w-0 flex-1">
                {!grouped && (
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium">{message.author?.name ?? 'Someone'}</span>
                    <span className="text-xs text-muted-foreground">{timeAgo(message.createdAt)}</span>
                  </div>
                )}

                {message.replyTo && (
                  <div className="mb-1 border-l-2 border-border pl-2 text-xs text-muted-foreground">
                    <span className="font-medium">{message.replyTo.author?.name ?? 'Someone'}</span>
                    <span className="ml-1.5 line-clamp-1">{message.replyTo.body}</span>
                  </div>
                )}

                {editing === message.id ? (
                  <div className="flex items-end gap-2 py-1">
                    <Textarea
                      value={editDraft}
                      onChange={(event) => setEditDraft(event.target.value)}
                      rows={1}
                      className="min-h-[34px] min-w-0 flex-1 resize-none text-sm"
                    />
                    <Button
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => {
                        edit.mutate(
                          { id: message.id, body: editDraft },
                          { onSuccess: () => setEditing(null) },
                        );
                      }}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 shrink-0"
                      onClick={() => setEditing(null)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap break-words text-sm">
                    {message.body}
                    {message.editedAt && (
                      <span className="ml-1.5 text-xs text-muted-foreground">(edited)</span>
                    )}
                    {message.author?.id === meId && (
                      <DeliveryTicks message={message} meId={meId} members={members} />
                    )}
                  </p>
                )}

                <Reactions
                  reactions={message.reactions}
                  meId={meId}
                  onToggle={(emoji) => react.mutate({ id: message.id, emoji })}
                />
              </div>

              {/* Revealed on hover for a mouse, on focus for a keyboard, and
                  always on a device with no hover at all — the actions were
                  simply unreachable on a phone before. `pointer-events-none`
                  while invisible so it does not swallow clicks on the message
                  underneath it. */}
              <div className="absolute right-2 top-0 flex items-center gap-0.5 rounded-md border bg-background p-0.5 opacity-0 shadow-sm transition-opacity pointer-events-none group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100">
                {QUICK_REACTIONS.slice(0, 3).map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => react.mutate({ id: message.id, emoji })}
                    className="rounded px-1 text-sm hover:bg-muted"
                    title={`React ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
                <button
                  onClick={() => onReply(message)}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  title="Reply"
                >
                  <SmilePlus className="h-3.5 w-3.5" />
                </button>
                {message.author?.id === meId && (
                  <>
                    <button
                      onClick={() => {
                        setEditing(message.id);
                        setEditDraft(message.body);
                      }}
                      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => remove.mutate(message.id)}
                      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Reactions({
  reactions,
  meId,
  onToggle,
}: {
  reactions: { emoji: string; userId: string }[] | undefined;
  meId: string;
  onToggle: (emoji: string) => void;
}) {
  // Tolerates a message that arrived without the field. The server sends it on
  // every path now, but a render that throws takes the whole page down with
  // it, and one missing array is not worth that.
  if (!reactions || reactions.length === 0) return null;

  const grouped = new Map<string, string[]>();
  for (const reaction of reactions) {
    grouped.set(reaction.emoji, [...(grouped.get(reaction.emoji) ?? []), reaction.userId]);
  }

  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {[...grouped.entries()].map(([emoji, userIds]) => (
        <button
          key={emoji}
          onClick={() => onToggle(emoji)}
          className={cn(
            'flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs transition-colors',
            userIds.includes(meId)
              ? 'border-primary/40 bg-primary/10'
              : 'hover:bg-muted',
          )}
        >
          <span>{emoji}</span>
          <span className="tabular text-muted-foreground">{userIds.length}</span>
        </button>
      ))}
    </div>
  );
}

// ── Dialogs ─────────────────────────────────────────────────────────────────

function ComposeDialog({ kind, onClose }: { kind: 'channel' | 'direct'; onClose: () => void }) {
  const navigate = useNavigate();
  const { data: users } = useUserOptions();
  const { data: me } = useSession();

  const { can } = usePermission();
  const mayOpenToRole = can('chat.channel.manage');

  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  /** Who can see it. The only real decision on this form. */
  const [access, setAccess] = useState<'PUBLIC' | 'PRIVATE' | 'ROLE'>('PUBLIC');
  const [selected, setSelected] = useState<string[]>([]);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  // Only asked for when a role room is actually being built.
  const { data: roles = [] } = useChatRoles(kind === 'channel' && mayOpenToRole);

  const saveChannel = useSaveChannel();
  const openDirect = useOpenDirect();
  const openGroup = useOpenGroup();

  const colleagues = (users ?? []).filter(
    (user) =>
      user.id !== me?.id &&
      (!search || user.name.toLowerCase().includes(search.toLowerCase())),
  );

  const pending = saveChannel.isPending || openDirect.isPending || openGroup.isPending;
  const error = saveChannel.error ?? openDirect.error ?? openGroup.error;

  const go = (channel: { id: string }) => {
    onClose();
    navigate(`/chat/${channel.id}`);
  };

  const submit = () => {
    if (kind === 'channel') {
      saveChannel.mutate(
        {
          name,
          topic,
          kind: access,
          // A role room fills itself from the roles; picking people by hand
          // would only be overwritten by the next sync.
          ...(access === 'ROLE' ? { roleIds } : { memberIds: selected }),
        },
        { onSuccess: go },
      );
      return;
    }
    // One person is a direct message; more than one is a group.
    if (selected.length === 1) {
      openDirect.mutate(selected[0], { onSuccess: go });
    } else if (selected.length > 1) {
      openGroup.mutate(selected, { onSuccess: go });
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{kind === 'channel' ? 'New channel' : 'New conversation'}</DialogTitle>
          <DialogDescription>
            {kind === 'channel'
              ? 'A named room. Choose who can see it, then who is in it.'
              : 'Pick one colleague for a direct message, or several for a group.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {kind === 'channel' && (
            <>
              <div>
                <Label htmlFor="channel-name">Name</Label>
                <Input
                  id="channel-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="delivery-standup"
                />
              </div>
              <div>
                <Label htmlFor="channel-topic">What it is for</Label>
                <Input
                  id="channel-topic"
                  value={topic}
                  onChange={(event) => setTopic(event.target.value)}
                  placeholder="Daily updates from the delivery team"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Who can see it</Label>
                {ACCESS_CHOICES.filter((choice) => choice.value !== 'ROLE' || mayOpenToRole).map(
                  (choice) => (
                    <label
                      key={choice.value}
                      className={cn(
                        'flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-sm transition-colors',
                        access === choice.value ? 'border-primary bg-primary/5' : 'hover:bg-muted',
                      )}
                    >
                      <input
                        type="radio"
                        name="channel-access"
                        className="mt-0.5"
                        checked={access === choice.value}
                        onChange={() => setAccess(choice.value)}
                      />
                      <span>
                        <span className="block font-medium">{choice.label}</span>
                        <span className="block text-xs text-muted-foreground">{choice.hint}</span>
                      </span>
                    </label>
                  ),
                )}
              </div>

              {access === 'ROLE' && (
                <div>
                  <Label>Which roles</Label>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Everyone holding one of these is added, and stays in only while they hold it.
                  </p>
                  <div className="max-h-44 space-y-0.5 overflow-y-auto rounded-md border p-1">
                    {roles.map((role) => {
                      const picked = roleIds.includes(role.id);
                      return (
                        <button
                          key={role.id}
                          onClick={() =>
                            setRoleIds((current) =>
                              picked
                                ? current.filter((id) => id !== role.id)
                                : [...current, role.id],
                            )
                          }
                          className={cn(
                            'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors',
                            picked ? 'bg-primary/10' : 'hover:bg-muted',
                          )}
                        >
                          <Shield className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="min-w-0 flex-1 truncate">{role.name}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {role.userCount} {role.userCount === 1 ? 'person' : 'people'}
                          </span>
                          {picked && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                        </button>
                      );
                    })}
                    {roles.length === 0 && (
                      <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                        No roles yet
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {!(kind === 'channel' && access === 'ROLE') && (
          <div>
            <Label htmlFor="member-search">
              {kind === 'channel' ? 'Add people' : 'Who are you messaging?'}
            </Label>
            <Input
              id="member-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search colleagues"
              className="mb-2"
            />
            <div className="max-h-52 space-y-0.5 overflow-y-auto rounded-md border p-1">
              {colleagues.map((user) => {
                const picked = selected.includes(user.id);
                return (
                  <button
                    key={user.id}
                    onClick={() =>
                      setSelected((current) =>
                        picked ? current.filter((id) => id !== user.id) : [...current, user.id],
                      )
                    }
                    className={cn(
                      'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors',
                      picked ? 'bg-primary/10' : 'hover:bg-muted',
                    )}
                  >
                    <Avatar name={user.name} size={24} />
                    <span className="min-w-0 flex-1 truncate">{user.name}</span>
                    {user.designation && (
                      <span className="truncate text-xs text-muted-foreground">{user.designation}</span>
                    )}
                    {picked && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                  </button>
                );
              })}
              {colleagues.length === 0 && (
                <p className="px-2 py-3 text-center text-sm text-muted-foreground">Nobody found</p>
              )}
            </div>
          </div>
          )}

          {error && <FormError message={(error as ApiRequestError).message} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              pending ||
              (kind === 'channel'
                ? !name.trim() || (access === 'ROLE' && roleIds.length === 0)
                : selected.length === 0)
            }
          >
            {kind === 'channel' ? 'Create channel' : 'Start conversation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembersDialog({
  channel,
  meId,
  onClose,
}: {
  channel: ChatChannel;
  meId: string;
  onClose: () => void;
}) {
  const { data: users } = useUserOptions();
  const update = useUpdateChannelMembers(channel.id);
  const leave = useLeaveChannel();
  const navigate = useNavigate();

  const memberIds = new Set(channel.members.map((member) => member.userId));
  const canManage = channel.members.find((member) => member.userId === meId)?.isAdmin ?? false;
  const isDirect = channel.kind === 'DIRECT';

  const addable = (users ?? []).filter((user) => !memberIds.has(user.id));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{channel.title}</DialogTitle>
          <DialogDescription>
            {channel.members.length} {channel.members.length === 1 ? 'person' : 'people'} in this
            conversation.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-64 space-y-1 overflow-y-auto">
          {channel.members.map((member) => (
            <div key={member.userId} className="flex items-center gap-2 rounded px-1 py-1.5 text-sm">
              <Avatar name={member.user.name} size={26} />
              <span className="min-w-0 flex-1 truncate">{member.user.name}</span>
              {member.isAdmin && <Badge variant="secondary">Admin</Badge>}
              {canManage && !isDirect && member.userId !== meId && (
                <button
                  onClick={() => update.mutate({ remove: [member.userId] })}
                  className="text-muted-foreground hover:text-destructive"
                  title="Remove"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>

        {canManage && !isDirect && addable.length > 0 && (
          <div>
            <Label>Add someone</Label>
            <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-md border p-1">
              {addable.map((user) => (
                <button
                  key={user.id}
                  onClick={() => update.mutate({ add: [user.id] })}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                >
                  <Avatar name={user.name} size={22} />
                  <span className="min-w-0 flex-1 truncate">{user.name}</span>
                  <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {!isDirect && (
            <Button
              variant="outline"
              onClick={() =>
                leave.mutate(channel.id, {
                  onSuccess: () => {
                    onClose();
                    navigate('/chat');
                  },
                })
              }
            >
              Leave channel
            </Button>
          )}
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Who can read a room.
 *
 * Three answers, and the wording is the whole point: somebody setting up a
 * channel is not choosing a "kind", they are deciding who will be able to read
 * what is said in it.
 */
const ACCESS_CHOICES = [
  {
    value: 'PUBLIC' as const,
    label: 'Anyone in the workspace',
    hint: 'Listed for everybody. They join it themselves.',
  },
  {
    value: 'PRIVATE' as const,
    label: 'Only the people I add',
    hint: 'Nobody else can see it, or that it exists.',
  },
  {
    value: 'ROLE' as const,
    label: 'Everyone with a role',
    hint: 'Membership follows the role. Lose the role, lose the room.',
  },
];

/**
 * The receipt on your own message: sent, delivered, read.
 *
 * One tick means the server has it. Two mean every other person in the
 * conversation has picked it up — their app polls wherever it is open, so this
 * is the honest equivalent of "it reached their phone". Blue means every one
 * of them has actually opened the conversation since.
 *
 * "Every one of them" matters in a group: a room of nine goes blue only when
 * the ninth person has looked, the same way WhatsApp does it.
 */
function DeliveryTicks({
  message,
  meId,
  members,
}: {
  message: ChatMessage;
  meId: string;
  members: ChatMember[];
}) {
  const sentAt = Date.parse(message.createdAt);
  const others = members.filter((member) => member.userId !== meId);

  const since = (stamp: string | null) => stamp !== null && Date.parse(stamp) >= sentAt;
  // A room with nobody else in it can never get past one tick, and saying it
  // was delivered to an empty room would be a lie.
  const read = others.length > 0 && others.every((member) => since(member.lastReadAt));
  const delivered =
    others.length > 0 && others.every((member) => since(member.lastDeliveredAt));

  const label = read ? 'Read' : delivered ? 'Delivered' : 'Sent';

  return (
    <span
      title={label}
      className={cn(
        'ml-1.5 inline-flex translate-y-[2px] align-baseline',
        read ? 'text-sky-500' : 'text-muted-foreground',
      )}
    >
      {delivered ? <CheckCheck className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
      <span className="sr-only">{label}</span>
    </span>
  );
}
